import { syncNestedCampaignName } from '../../../../utils/syncNestedCampaignName';

/**
 * useTaskEdit - Rename, resize, icon, and basic property changes
 * 
 * @param {Object} localSprint - Current sprint state
 * @param {Function} setLocalSprint - State setter for local sprint
 * @param {Function} recordAction - Function to record undoable actions (optional)
 * @param {Object} options - Additional options
 * @param {Array} options.nestedSprints - Array of nested sprints for name sync
 * @param {Function} options.onUpdateSprint - Callback to update a sprint (for syncing nested campaign name)
 */

export const useTaskEdit = (localSprint, setLocalSprint, recordAction, options = {}) => {
  const { nestedSprints = [], onUpdateSprint } = options;

  const syncNestedName = (nestedSprint, name) => {
    if (!nestedSprint || typeof name !== 'string') return;
    syncNestedCampaignName(nestedSprint, name, { onUpdateSprint });
  };

  const handleRenameTask = (taskId, newName) => {
    const task = localSprint?.tasks?.find(t => t.id === taskId);
    const oldName = task?.name;

    if (oldName === newName) return;

    // If this task has a nested sprint, sync the campaign name too
    const nestedSprintId = task?.nestedSprintId;
    const nestedSprint = nestedSprintId 
      ? nestedSprints.find(ns => ns.id === nestedSprintId) 
      : null;

    setLocalSprint(prev => {
      if (!prev) return prev;
      const tasks = prev.tasks || [];
      return {
        ...prev,
        tasks: tasks.map(t => 
          t.id === taskId ? { ...t, name: newName, updatedAt: new Date().toISOString() } : t
        ),
      };
    });

    // Nested campaign name stays locked to the mission. Shells (no tasks) must
    // be fetched before save — otherwise the pill updates and then snaps back.
    syncNestedName(nestedSprint, newName);

    // Record for undo
    if (recordAction && oldName !== newName) {
      // Truncate names for display
      const truncate = (s, len = 20) => s && s.length > len ? s.slice(0, len) + '...' : s;
      const oldNestedName = nestedSprint?.name;
      
      recordAction({
        type: 'RENAME_TASK',
        description: `Rename: "${truncate(oldName)}" → "${truncate(newName)}"`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => t.id === taskId ? { ...t, name: oldName } : t),
            };
          });
          syncNestedName(nestedSprint, oldNestedName);
        },
        redo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => t.id === taskId ? { ...t, name: newName } : t),
            };
          });
          syncNestedName(nestedSprint, newName);
        },
      });
    }
  };

  const handleEditTask = (task) => {
    const newName = prompt('Edit mission name:', task.name);
    if (!newName || newName === task.name) return;
    handleRenameTask(task.id, newName);
  };

  const handleTaskResize = (taskId, { width, height, newX, newY, positionChanged }) => {
    setLocalSprint(prev => {
      if (!prev) return prev;
      const tasks = prev.tasks || [];
      return {
        ...prev,
        tasks: tasks.map(t => {
          if (t.id !== taskId) return t;
          const updated = { ...t, width, height };
          // If resizing from north or west edge, position also changes
          if (positionChanged && newX !== undefined && newY !== undefined) {
            updated.x = newX;
            updated.y = newY;
          }
          return updated;
        }),
      };
    });
    // Note: Undo is recorded in handleTaskResizeEnd when drag completes
  };

  // Called when resize drag completes - records undo action
  const handleTaskResizeEnd = (taskId, { oldWidth, oldHeight, oldX, oldY, newWidth, newHeight, newX, newY }) => {
    if (!recordAction) return;
    
    const task = localSprint?.tasks?.find(t => t.id === taskId);
    const taskName = task?.name || 'Node';
    
    recordAction({
      type: 'RESIZE_NODE',
      description: `Resize: "${taskName}"`,
      undo: () => {
        setLocalSprint(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            tasks: prev.tasks.map(t => 
              t.id === taskId 
                ? { ...t, width: oldWidth, height: oldHeight, x: oldX, y: oldY }
                : t
            ),
          };
        });
      },
      redo: () => {
        setLocalSprint(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            tasks: prev.tasks.map(t => 
              t.id === taskId 
                ? { ...t, width: newWidth, height: newHeight, x: newX, y: newY }
                : t
            ),
          };
        });
      },
    });
  };

  // Reset custom size (double-click on resize handle) - returns to auto-sizing
  const handleTaskResetSize = (taskId) => {
    const task = localSprint?.tasks?.find(t => t.id === taskId);
    if (!task) return;
    
    const oldWidth = task.width;
    const oldHeight = task.height;
    
    // Only proceed if there's actually a custom size to reset
    if (oldWidth === undefined && oldHeight === undefined) return;
    
    setLocalSprint(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        tasks: prev.tasks.map(t => {
          if (t.id !== taskId) return t;
          // Remove custom width/height to return to auto-sizing
          const { width, height, ...rest } = t;
          return { ...rest, updatedAt: new Date().toISOString() };
        }),
      };
    });
    
    // Record for undo
    if (recordAction) {
      const taskName = task?.name || 'Node';
      recordAction({
        type: 'RESET_NODE_SIZE',
        description: `Reset size: "${taskName}"`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => 
                t.id === taskId 
                  ? { ...t, width: oldWidth, height: oldHeight }
                  : t
              ),
            };
          });
        },
        redo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => {
                if (t.id !== taskId) return t;
                const { width, height, ...rest } = t;
                return rest;
              }),
            };
          });
        },
      });
    }
  };

  const handleIconChange = (taskId, icon) => {
    const task = localSprint?.tasks?.find(t => t.id === taskId);
    const oldIcon = task?.icon;

    setLocalSprint(prev => {
      if (!prev) return prev;
      const tasks = prev.tasks || [];
      return {
        ...prev,
        tasks: tasks.map(t => 
          t.id === taskId ? { ...t, icon, updatedAt: new Date().toISOString() } : t
        ),
      };
    });

    // Record for undo
    if (recordAction) {
      const taskName = task?.name || 'Task';
      recordAction({
        type: 'CHANGE_ICON',
        description: `${taskName}: Change icon`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => t.id === taskId ? { ...t, icon: oldIcon } : t),
            };
          });
        },
        redo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => t.id === taskId ? { ...t, icon } : t),
            };
          });
        },
      });
    }
  };

  // Clear a specific custom icon from ALL tasks in the current sprint
  // Called when a custom icon is deleted from the icon library
  const clearDeletedIconFromAllTasks = (deletedIconId) => {
    if (!localSprint?.tasks) return;
    
    // Find all tasks that use this icon
    const affectedTasks = localSprint.tasks.filter(t => 
      t.icon?.type === 'custom' && t.icon?.iconId === deletedIconId
    );
    
    if (affectedTasks.length === 0) return;
    
    setLocalSprint(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        tasks: prev.tasks.map(t => 
          t.icon?.type === 'custom' && t.icon?.iconId === deletedIconId
            ? { ...t, icon: null }
            : t
        ),
      };
    });
    
    // Note: Not recording undo for this since it's a cleanup action
    // triggered by icon deletion, not a direct user action on tasks
  };

  // Set whether a task's status should auto-sync with its nested sprint progress
  // When false, the user has manually overridden the status
  // When true (or undefined), status auto-syncs based on nested sprint progress
  // 
  // Optional: newStatus can be provided to change status in the same atomic update
  // This prevents race conditions where useProgressSync might override the status
  const handleSetStatusAutoSync = (taskId, autoSync, newStatus = null) => {
    const task = localSprint?.tasks?.find(t => t.id === taskId);
    const oldAutoSync = task?.statusAutoSync;
    const oldStatus = task?.status;
    
    setLocalSprint(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        tasks: prev.tasks.map(t => {
          if (t.id !== taskId) return t;
          const updates = { 
            ...t, 
            statusAutoSync: autoSync, 
            updatedAt: new Date().toISOString() 
          };
          // If a new status is provided, set it in the same update
          if (newStatus !== null) {
            updates.status = newStatus;
          }
          return updates;
        }),
      };
    });

    // Record for undo
    if (recordAction) {
      const taskName = task?.name || 'Task';
      const description = autoSync 
        ? `${taskName}: Enable auto-sync` 
        : `${taskName}: Manual status override`;
      recordAction({
        type: 'SET_STATUS_AUTO_SYNC',
        description,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => {
                if (t.id !== taskId) return t;
                const updates = { ...t, statusAutoSync: oldAutoSync };
                if (newStatus !== null) {
                  updates.status = oldStatus;
                }
                return updates;
              }),
            };
          });
        },
        redo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => {
                if (t.id !== taskId) return t;
                const updates = { ...t, statusAutoSync: autoSync };
                if (newStatus !== null) {
                  updates.status = newStatus;
                }
                return updates;
              }),
            };
          });
        },
      });
    }
  };

  const handleMilestoneAnchorChange = (taskId, milestoneAnchor) => {
    const task = localSprint?.tasks?.find((t) => t.id === taskId);
    if (!task || task.nodeType !== 'milestone') return;
    const oldAnchor = task.milestoneAnchor || null;

    setLocalSprint((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        tasks: prev.tasks.map((t) =>
          t.id === taskId
            ? { ...t, milestoneAnchor, updatedAt: new Date().toISOString() }
            : t,
        ),
      };
    });

    if (recordAction) {
      recordAction({
        type: 'MILESTONE_ANCHOR',
        description: 'Update milestone attachment',
        undo: () => {
          setLocalSprint((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map((t) =>
                t.id === taskId ? { ...t, milestoneAnchor: oldAnchor } : t,
              ),
            };
          });
        },
        redo: () => {
          setLocalSprint((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map((t) =>
                t.id === taskId ? { ...t, milestoneAnchor } : t,
              ),
            };
          });
        },
      });
    }
  };

  return {
    handleRenameTask,
    handleEditTask,
    handleTaskResize,
    handleTaskResizeEnd,
    handleTaskResetSize,
    handleIconChange,
    clearDeletedIconFromAllTasks,
    handleSetStatusAutoSync,
    handleMilestoneAnchorChange,
  };
};

export default useTaskEdit;

